import React from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaFileContract, FaFileSignature, FaUmbrellaBeach, FaCalendarAlt, FaDownload } from 'react-icons/fa';
import Tile from '../../../components/Tile';

const Documents = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const documents = [
    {
      id: 1,
      title: 'Rozwiązanie umowy za porozumieniem stron',
      description: 'Wzór dokumentu do natychmiastowego rozwiązania umowy o naukę zawodu',
      icon: <FaFileContract />,
      category: 'Rozwiązanie umowy',
      gradient: 'blue'
    },
    {
      id: 2,
      title: 'Rozwiązanie umowy z wypowiedzeniem',
      description: 'Wzór dokumentu z zachowaniem okresu wypowiedzenia',
      icon: <FaFileSignature />,
      category: 'Rozwiązanie umowy',
      gradient: 'purple'
    },
    {
      id: 3,
      title: 'Wniosek o urlop bezpłatny (poniżej 18 lat)',
      description: 'Wzór wniosku dla młodocianych przed 18 rokiem życia (art. 205 § 4 KP)',
      icon: <FaCalendarAlt />,
      category: 'Urlopy',
      gradient: 'pink'
    },
    {
      id: 4,
      title: 'Wniosek o urlop bezpłatny (powyżej 18 lat)',
      description: 'Wzór wniosku dla osób po 18 roku życia (art. 174 § 2 KP)',
      icon: <FaCalendarAlt />,
      category: 'Urlopy',
      gradient: 'green'
    },
    {
      id: 5,
      title: 'Wniosek o urlop wypoczynkowy',
      description: 'Wzór wniosku o udzielenie urlopu wypoczynkowego',
      icon: <FaUmbrellaBeach />,
      category: 'Urlopy',
      gradient: 'blue'
    }
  ];

  const documentTemplates = {
    1: {
      filename: 'rozwiazanie_umowy_porozumienie.pdf',
    },
    2: {
      filename: 'rozwiazanie_umowy_wypowiedzenie.pdf',
    },
    3: {
      filename: 'urlop_bezplatny_ponizej_18.pdf',
    },
    4: {
      filename: 'urlop_bezplatny_powyzej_18.pdf',
    },
    5: {
      filename: 'urlop_wypoczynkowy.pdf',
    }
  };

  const handleDownload = async (docId) => {
    const template = documentTemplates[docId];
    if (!template) {
      alert('Nie znaleziono szablonu dokumentu');
      return;
    }

    try {
      // URL do backendu Django - dostosuj do swojego adresu
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const downloadUrl = `${backendUrl}/api/documents/download/${template.filename}`;
      
      // Pobierz plik z backendu
      const response = await fetch(downloadUrl);
      
      if (!response.ok) {
        throw new Error('Nie udało się pobrać pliku');
      }
      
      // Pobierz blob
      const blob = await response.blob();
      
      // Utwórz URL do pobrania
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = template.filename;
      document.body.appendChild(link);
      link.click();
      
      // Posprzątaj
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Błąd pobierania:', error);
      alert('Wystąpił błąd podczas pobierania pliku. Spróbuj ponownie później.');
    }
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    if (!acc[doc.category]) {
      acc[doc.category] = [];
    }
    acc[doc.category].push(doc);
    return acc;
  }, {});

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      width: '100%'
    }}>
      <Container>
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-5"
        >
          <h1 className="text-white fw-bold mb-3">
            Wzory dokumentów
          </h1>
          <p className="text-white-50" style={{ fontSize: '1.2rem' }}>
            Gotowe szablony dokumentów do pobrania w formacie PDF
          </p>
        </motion.div>

        <Row className="justify-content-center">
          <Col xs={12} lg={10}>
            {/* INFO CARD */}
            <motion.div
              {...fadeInUp}
              className="mb-5"
            >
              <Card className="border-0 shadow-lg" style={{ 
                borderRadius: '20px',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)'
              }}>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-center mb-3">
                    <FaDownload style={{ fontSize: '2rem', color: '#667eea', marginRight: '1rem' }} />
                    <h5 className="mb-0 fw-bold">Jak korzystać z wzorów?</h5>
                  </div>
                  <ul className="mb-0" style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                    <li>Kliknij na wybrany dokument, aby pobrać go w formacie PDF</li>
                    <li>Wypełnij wszystkie wymagane pola</li>
                    <li>Pamiętaj o podpisie - Twoim i rodzica/opiekuna (jeśli dotyczy)</li>
                    <li>Przygotuj <strong>2 egzemplarze</strong> - jeden dla pracodawcy, jeden dla siebie</li>
                  </ul>
                </Card.Body>
              </Card>
            </motion.div>

            {/* DOCUMENTS BY CATEGORY */}
            {Object.entries(groupedDocuments).map(([category, docs], categoryIndex) => (
              <motion.div
                key={category}
                {...fadeInUp}
                transition={{ delay: 0.1 * (categoryIndex + 1) }}
                className="mb-5"
              >
                <h3 className="text-white mb-4 fw-bold">
                  {category}
                </h3>
                <Row>
                  {docs.map((doc, index) => (
                    <Col 
                      key={doc.id} 
                      xs={12} 
                      md={6} 
                      lg={4} 
                      className="mb-4"
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                      >
                        <Tile
                          title={doc.title}
                          description={doc.description}
                          icon={doc.icon}
                          gradient={doc.gradient}
                          onClick={() => handleDownload(doc.id)}
                        />
                      </motion.div>
                    </Col>
                  ))}
                </Row>
              </motion.div>
            ))}

            {/* IMPORTANT NOTE */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.5 }}
              className="mt-5"
            >
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '20px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '2px solid rgba(255, 193, 7, 0.3)'
                }}
              >
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3">
                    <FaFileContract className="me-2" style={{ color: '#ffc107' }} />
                    Ważne informacje
                  </h5>
                  <ul className="mb-0" style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                    <li className="mb-2">
                      <strong>Forma pisemna jest obowiązkowa</strong> - dzięki temu można potwierdzić 
                      fakt złożenia wniosku lub rozwiązania umowy
                    </li>
                    <li className="mb-2">
                      Dokumenty dotyczące rozwiązania umowy wymagają podpisu <strong>młodocianego 
                      oraz rodzica/opiekuna</strong>, ponieważ oboje są stronami umowy
                    </li>
                    <li className="mb-2">
                      Wniosek o urlop bezpłatny przed 18 rokiem życia powołuje się na <strong>art. 205 § 4 
                      Kodeksu pracy</strong>
                    </li>
                    <li>
                      Wniosek o urlop bezpłatny po 18 roku życia powołuje się na <strong>art. 174 § 2 
                      Kodeksu pracy</strong>
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Documents;